import { createFileRoute, useLocation } from "@tanstack/react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Shield, BarChart, FileSearch, CheckCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SEO } from "@/components/SEO"
import { StructuredData } from "@/components/StructuredData"
import { pharmaceuticalPage, siteMetadata } from "@/content"

export const Route = createFileRoute("/industries/pharmaceutical")({
  component: PharmaceuticalPage,
})

const iconMap = {
  shield: Shield,
  barChart: BarChart,
  fileSearch: FileSearch,
  checkCircle: CheckCircle,
}

function PharmaceuticalPage() {
  const location = useLocation()

  return (
    <>
      <SEO metadata={pharmaceuticalPage.seo} />
      <StructuredData
        type="about"
        pageTitle="Pharmaceutical AI Solutions"
        pageUrl={`${siteMetadata.url}${location.pathname}`}
      />
      <div className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            {pharmaceuticalPage.header.badge && (
              <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-primary bg-primary/10 rounded-full">
                {pharmaceuticalPage.header.badge}
              </span>
            )}
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{pharmaceuticalPage.header.title}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{pharmaceuticalPage.header.subtitle}</p>
          </div>

          {/* Challenges Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{pharmaceuticalPage.challenges.title}</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {pharmaceuticalPage.challenges.subtitle}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {pharmaceuticalPage.challenges.items.map((challenge, index) => {
                const Icon = iconMap[challenge.icon as keyof typeof iconMap]
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="shrink-0">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold mb-2">{challenge.title}</h3>
                          <p className="text-muted-foreground">{challenge.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </section>

          {/* Solutions Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{pharmaceuticalPage.solutions.title}</h2>
            </div>
            <div className="space-y-8">
              {pharmaceuticalPage.solutions.offerings.map((offering, index) => (
                <Card key={index}>
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-semibold mb-4">{offering.title}</h3>
                    <p className="text-muted-foreground mb-6">{offering.description}</p>
                    <ul className="space-y-2 mb-6">
                      {offering.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild>
                      <a href={offering.cta.href}>
                        {offering.cta.text}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Use Cases Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{pharmaceuticalPage.useCases.title}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {pharmaceuticalPage.useCases.examples.map((useCase, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-4">{useCase.title}</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-semibold text-muted-foreground">Challenge:</span>
                        <p className="mt-1">{useCase.challenge}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-muted-foreground">Solution:</span>
                        <p className="mt-1">{useCase.solution}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-primary">Result:</span>
                        <p className="mt-1 font-medium">{useCase.result}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Compliance Features Section */}
          {pharmaceuticalPage.complianceFeatures && (
            <section className="mb-20">
              <Card className="bg-primary/5">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6">{pharmaceuticalPage.complianceFeatures.title}</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {pharmaceuticalPage.complianceFeatures.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {/* CTA Section */}
          <section className="text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-purple-500/10">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-4">{pharmaceuticalPage.cta.title}</h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  {pharmaceuticalPage.cta.subtitle}
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  {pharmaceuticalPage.cta.buttons.map((button, index) => (
                    <Button key={index} asChild variant={button.primary ? "default" : "outline"} size="lg">
                      <a href={button.href}>{button.text}</a>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </>
  )
}
